import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import {
  useTheme,
  type CourseBlock,
  type LessonCompleteBlock,
  type TranslationRecordStatus,
} from '@course-authoring/shared-types';
import { LessonCompleteEditor } from './LessonCompleteEditor';
import { TranslationStatusBadge } from './TranslationStatusPrimitives';

interface LessonCompleteSectionProps {
  block: LessonCompleteBlock;
  onChange: (updates: Partial<CourseBlock>) => void;
  isLocked?: boolean;
  onToggleLock?: () => void;
  translationStatus?: TranslationRecordStatus | null;
  translationNote?: React.ReactNode;
  translationMode?: boolean;
}

export function LessonCompleteSection({
  block,
  onChange,
  isLocked = false,
  onToggleLock,
  translationStatus,
  translationNote,
  translationMode = false,
}: LessonCompleteSectionProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  return (
    <div style={styles.container} data-block-id={block.id}>
      <div style={styles.headerRow}>
        <div style={styles.label}>LESSON COMPLETE</div>
        {translationStatus ? (
          <TranslationStatusBadge status={translationStatus} missingLabel="Not Translated" />
        ) : null}
        {isLocked && (
          <span style={styles.lockedBadge}>
            <FontAwesomeIcon icon={faLock} style={{ fontSize: '11px' }} />
            Locked
          </span>
        )}
        {onToggleLock ? (
          <button
            type="button"
            onClick={onToggleLock}
            style={{
              ...styles.lockToggle,
              ...(isLocked ? styles.lockToggleLocked : {}),
            }}
            title={isLocked ? 'Unlock block' : 'Lock block'}
            aria-label={isLocked ? 'Unlock block' : 'Lock block'}
            aria-pressed={isLocked}
          >
            <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
          </button>
        ) : null}
      </div>
      {translationNote}
      <div
        style={isLocked ? { opacity: 0.6 } : undefined}
        inert={isLocked || undefined}
      >
        <LessonCompleteEditor block={block} onChange={onChange} translationMode={translationMode} />
      </div>
    </div>
  );
}

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
): Record<string, CSSProperties> {
  return {
    container: {
      width: '100%',
      padding: `${tokens.spacing['2xl']} ${tokens.spacing.xl} ${tokens.spacing.xl}`,
      boxSizing: 'border-box',
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.lg,
      backgroundColor: tokens.backgrounds.app,
      boxShadow: tokens.shadow.sm,
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.md,
    },
    label: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '1px',
      color: tokens.text.subtle,
      fontFamily: tokens.typography.fontFamily,
      textTransform: 'uppercase',
      flex: 1,
    },
    lockedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.xs,
      minHeight: '26px',
      padding: `0 ${tokens.spacing.md}`,
      borderRadius: tokens.radius.full,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.muted,
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    lockToggle: {
      width: '32px',
      height: '32px',
      padding: 0,
      fontSize: '13px',
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.surface,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: tokens.shadow.sm,
      fontFamily: tokens.typography.fontFamily,
    },
    lockToggleLocked: {
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
    },
  };
}
